import _ from 'lodash';
import { DOM } from '../../browser-helpers/dom-factory';
import { Events } from '../../browser-constants/events';
import { BaseTool } from '../base-tool';
import { isDarkTheme } from '../../ui-helpers/is-dark-theme/is-dark-theme';
import { StateManager } from '../../toolbar-managers/state-manager/state-manager';
import { ShortcutKeys } from '../../browser-constants/shortcut-keys';
import { ElementManager } from '../../toolbar-managers/element-manager/element-manager';
import { LocalStorageKeys } from '../../browser-constants/local-storage-keys';
import { isShortcutKeyOnly } from '../../browser-helpers/is-shortcut-key-only';
import { TranslationManager } from '../../toolbar-managers/translation-manager/translation-manager';
import { SvgPaths, getSvgIcon } from '../../ui-icons/get-svg-icon/get-svg-icon';

const FILENAME = 'theme-tool.js';
const CLASS__TOOL_BUTTON = 'oltb-tool-button';
const I18N__BASE = 'tools.themeTool';

const DefaultOptions = Object.freeze({
  onInitiated: undefined,
  onClicked: undefined,
  onBrowserStateCleared: undefined,
  onChanged: undefined,
});

const ThemesData = Object.freeze({
  light: {
    class: 'light',
    icon: getSvgIcon({
      path: SvgPaths.moonStars.stroked,
      class: `${CLASS__TOOL_BUTTON}__icon`,
    }),
  },
  dark: {
    class: 'dark',
    icon: getSvgIcon({
      path: SvgPaths.sun.stroked,
      class: `${CLASS__TOOL_BUTTON}__icon`,
    }),
  },
});

const LocalStorageNodeName = LocalStorageKeys.themeTool;
const LocalStorageDefaults = Object.freeze({
  theme: ThemesData.light.class,
});

/**
 * About:
 * Change theme of the Toolbar
 *
 * Description:
 * The Toolbar is light by default, via this tool the Toolbar can be made dark to be better seen against a bright Map image.
 */
class ThemeTool extends BaseTool {
  constructor(options = {}) {
    super({
      filename: FILENAME,
    });

    const i18n = TranslationManager.get(I18N__BASE);
    const button = DOM.createElement({
      element: 'button',
      html: this.getToolIcon(),
      class: CLASS__TOOL_BUTTON,
      attributes: {
        type: 'button',
        'data-tippy-content': ShortcutKeys.themeTool ? `${i18n.title} (${ShortcutKeys.themeTool})` : `${i18n.title}`,
        'data-tippy-content-post': `(${ShortcutKeys.themeTool})`,
        'data-oltb-i18n': `${I18N__BASE}.title`,
      },
      listeners: {
        click: this.onClickTool.bind(this),
      },
      prototypes: {
        getTippy() {
          return this._tippy;
        },
      },
    });

    DOM.appendChildren(this.element, [button]);

    this.button = button;
    this.isActive = false;
    this.options = _.merge(_.cloneDeep(DefaultOptions), options);

    this.localStorage = StateManager.getAndMergeStateObject(LocalStorageNodeName, LocalStorageDefaults);

    this.attachGlobalListeners();

    // Note:
    // @Consumer callback
    if (this.options.onInitiated) {
      this.options.onInitiated();
    }
  }

  attachGlobalListeners() {
    this.onWindowKeyUpBind = this.#onWindowKeyUp.bind(this);
    this.onWindowBrowserStateClearedBind = this.#onWindowBrowserStateCleared.bind(this);

    window.addEventListener(Events.browser.keyUp, this.onWindowKeyUpBind);
    window.addEventListener(Events.custom.browserStateCleared, this.onWindowBrowserStateClearedBind);
  }

  detachGlobalListeners() {
    window.removeEventListener(Events.browser.keyUp, this.onWindowKeyUpBind);
    window.removeEventListener(Events.custom.browserStateCleared, this.onWindowBrowserStateClearedBind);
  }

  //--------------------------------------------------------------------
  // # Section: Overridden
  //--------------------------------------------------------------------
  getName() {
    return super.getName();
  }

  onClickTool(event) {
    super.onClickTool(event);
    this.momentaryActivation();

    // Note:
    // @Consumer callback
    if (this.options.onClicked) {
      this.options.onClicked();
    }
  }

  //--------------------------------------------------------------------
  // # Section: Tool Control
  //--------------------------------------------------------------------
  momentaryActivation() {
    this.doToggleTheme();
  }

  //--------------------------------------------------------------------
  // # Section: Browser Events
  //--------------------------------------------------------------------
  #onWindowKeyUp(event) {
    if (isShortcutKeyOnly(event, ShortcutKeys.themeTool)) {
      this.onClickTool(event);
    }
  }

  #onWindowBrowserStateCleared() {
    const active = this.getActiveTheme();
    this.doSwitchThemeFromTo(active, ThemesData.light);

    // Note:
    // @Consumer callback
    if (this.options.onBrowserStateCleared) {
      this.options.onBrowserStateCleared();
    }
  }

  //--------------------------------------------------------------------
  // # Section: Getters and Setters
  //--------------------------------------------------------------------
  getToolIcon() {
    return isDarkTheme() ? ThemesData.dark.icon : ThemesData.light.icon;
  }

  getInActiveThem() {
    return isDarkTheme() ? ThemesData.light : ThemesData.dark;
  }

  getActiveTheme() {
    return isDarkTheme() ? ThemesData.dark : ThemesData.light;
  }

  //--------------------------------------------------------------------
  // # Section: Tool DoActions
  //--------------------------------------------------------------------
  doToggleTheme() {
    const active = this.getActiveTheme();
    const inActive = this.getInActiveThem();

    this.doSwitchThemeFromTo(active, inActive);

    // Note:
    // @Consumer callback
    if (this.options.onChanged) {
      this.options.onChanged(inActive.class);
    }
  }

  doSwitchThemeFromTo(from, to) {
    this.localStorage.theme = to.class;
    StateManager.setStateObject(LocalStorageNodeName, this.localStorage);

    const baseClass = 'oltb';
    const uiRefToolbarElement = ElementManager.getToolbarElement();

    uiRefToolbarElement.classList.remove(from.class);
    window.document.body.classList.remove(`${baseClass}-${from.class}`);

    uiRefToolbarElement.classList.add(to.class);
    window.document.body.classList.add(`${baseClass}-${to.class}`);

    this.button.removeChild(this.button.firstElementChild);
    this.button.insertAdjacentHTML('afterbegin', to.icon);
  }
}

export { ThemeTool };
